'use client'

import Link from 'next/link'
import * as React from 'react'
import { useEffect, useState } from 'react'

import { NavigationLinkDTO } from '@/lib/models/model-types/navigation/navigation.types'
import {
  NavigationListItem,
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  navigationMenuTriggerStyle,
} from '@/lib/ui-kit/components/ui/NavigationMenu.component'
import TriviaButton from '@/lib/ui-kit/components/ui/TriviaButton.component'
import { cn } from '@/lib/utils'

import { Logo } from './ui/Logo'

interface NavHeaderProps {
  links: NavigationLinkDTO[]
  className?: string
  isScrollingUp?: boolean
  isAtTop?: boolean
  children?: React.ReactNode
  isBlog?: boolean
}

export const NavHeader = ({
  className,
  isScrollingUp,
  isAtTop,
  links,
  children,
  isBlog = false,
}: NavHeaderProps) => {
  const [submenuIsOpen, setSubmenuIsOpen] = useState(false)

  useEffect(() => {
    const submenuDiv = document.querySelector('.submenu-navigation')

    if (submenuDiv) {
      const hasChildren = submenuDiv.children.length > 0
      setSubmenuIsOpen(hasChildren)

      const observer = new MutationObserver(() => {
        const hasChildren = submenuDiv.children.length > 0

        if (hasChildren) {
          setSubmenuIsOpen(true)
        } else {
          setSubmenuIsOpen(false)
        }
      })

      observer.observe(submenuDiv, { childList: true })

      return () => observer.disconnect()
    }
  }, [])

  return (
    <NavigationMenu
      className={cn(
        className,
        {
          'main-navigation bg-base-light bg-opacity-50 shadow-none shadow-gray-600/[15%]':
            !isAtTop || submenuIsOpen,
          'scroll-up': !isScrollingUp,
        },
        'fixed z-50 hidden transition-all duration-500 ease-in-out xl:flex xl:flex-col xl:gap-2'
      )}
    >
      <section className="relative flex w-full justify-between xl:max-w-[1440px]">
        <Link href="/" className="h-[48px] w-[175px]">
          <Logo />
        </Link>
        <TriviaButton />
        <NavigationMenuList>
          {links.map(({ label, link, subMenu }, index) => {
            if (subMenu.length === 0) {
              return (
                <NavigationMenuItem key={link} className="h-full">
                  <NavigationMenuLink
                    className={`${navigationMenuTriggerStyle()} h-full`}
                    asChild
                  >
                    <Link href={link}>{label}</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )
            }

            return (
              <NavigationMenuItem key={index} className="h-full">
                <NavigationMenuTrigger
                  className="h-full"
                  onClick={(e) => e.preventDefault()}
                >
                  {link === '/' ? (
                    <span>{label}</span>
                  ) : (
                    <Link href={link}>{label}</Link>
                  )}
                </NavigationMenuTrigger>

                <NavigationMenuContent>
                  <div className="w-full p-10 pb-16 min-[1024px]:w-[1024px] min-[1500px]:w-[1500px]">
                    <ul className="grid gap-10 [direction:rtl] sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                      {subMenu.map(({ title, subLinks }, i) => (
                        <div
                          key={`${title || ''}-${i}`}
                          className="[direction:ltr]"
                        >
                          {title && (
                            <p className="paragraph-xxl font-semibold text-gray-600">
                              {title}
                            </p>
                          )}
                          {subLinks.map(({ link, label, description }, j) => (
                            <NavigationListItem
                              key={`${label}-${j}`}
                              href={link}
                              title={label}
                            >
                              {description}
                            </NavigationListItem>
                          ))}
                        </div>
                      ))}
                    </ul>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            )
          })}
        </NavigationMenuList>
        <NavigationMenuViewport isBlog={isBlog} className="z-50" />
      </section>

      {children}
    </NavigationMenu>
  )
}
